/**
 * The mirror room's timeline. Every value is a function of q = elapsed / 2700ms,
 * straight from the handoff; nothing here knows about React.
 *
 * Design-system exception: expo-out curves and a small back-out overshoot on the
 * ball. Approved in review for this one celebratory moment only, in the same
 * category as the other Lottie moments. Don't borrow these easings elsewhere.
 */
export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
export const seg = (q, a, b) => clamp((q - a) / (b - a), 0, 1);
export const outExpo = (p) => (p >= 1 ? 1 : 1 - 2 ** (-9 * p));
export const outCubic = (p) => 1 - (1 - p) ** 3;
export const outBack = (p) => { const c = 1.7; const u = p - 1; return 1 + (c + 1) * u ** 3 + c * u ** 2; };

export const TINTS = ['#FFCCE6', '#A8D8EA', '#C6B8FF', '#FFF8EA'];
export const LABEL = '[ INNER CIRCLE ]';

// y = % of row height, w/h px, v = speed, o = phase
export const SPOTS = [
  { y: 20, w: 12, h: 7, v: 1.0, o: 0.0 }, { y: 64, w: 8, h: 5, v: 0.72, o: 0.14 },
  { y: 40, w: 14, h: 8, v: 1.25, o: 0.28 }, { y: 80, w: 9, h: 6, v: 0.9, o: 0.42 },
  { y: 28, w: 7, h: 4, v: 1.4, o: 0.56 }, { y: 72, w: 12, h: 7, v: 0.8, o: 0.7 },
  { y: 52, w: 10, h: 6, v: 1.1, o: 0.84 }, { y: 12, w: 8, h: 5, v: 0.95, o: 0.98 },
];

/**
 * Per surface. `mr` is the mark's centre from the overlay's right edge, so in
 * rows the ball sits on the mark and it looks like the mark grows into the logo.
 * The dashboard panel pads its rows, so its room bleeds out by --cf-bleed (the
 * panel's padding) to reach the panel's inner edges.
 */
const BLEED = 'var(--cf-bleed, 24px)';
export const GEOMETRY = {
  card: { ball: 124, bx: '50%', light: 'beams', label: 14, pad: '0 0 12px 16px', justify: 'flex-end' },
  search: { ball: 46, bx: 'calc(100% - 28px)', mr: '28px', light: 'spots', label: 12, pad: '0 0 0 14px', justify: 'center' },
  searchMobile: { ball: 48, bx: 'calc(100% - 36px)', mr: '36px', light: 'spots', label: 12, pad: '0 0 0 14px', justify: 'center' },
  friends: { ball: 62, bx: 'calc(100% - 42px)', mr: '42px', light: 'spots', label: 13, pad: '0 0 0 24px', justify: 'center' },
  // /friends rows (Sept 2026 redesign): ~90px cards, the 40px mark in the action area.
  // Beams like a card, but the ball drops to 76px at 62% across so the label fits on the left.
  friendsRow: { ball: 76, bx: '62%', light: 'beams', len0: 44, len1: 520, bh: 14, label: 14, pad: '0 0 0 20px', justify: 'center' },
  // /search results page: 84px-tall bordered rows, 36px mark inside pr-4.
  searchPage: { ball: 62, bx: 'calc(100% - 34px)', mr: '34px', light: 'spots', label: 13, pad: '0 0 0 16px', justify: 'center' },
  // A friend's Portal Page: the whole viewport. Ball and beams are sized to
  // the screen when the room opens (see fullscreenGeometry).
  fullscreen: {
    fixed: true, ball: 320, bx: '50%', by: '44%', light: 'beams', len0: 80, len1: 900, bh: 28,
    label: 22, sub: 12, pad: '0 0 calc(40px + env(safe-area-inset-bottom)) 24px', justify: 'flex-end',
  },
  // Event guest list rows (12px 18px padding, 14px on mobile); measured too.
  guest: { ball: 50, bx: 'calc(100% - 34px)', mr: '34px', light: 'spots', label: 12, pad: '0 0 0 18px', justify: 'center' },
  dashboard: {
    inset: `0 calc(-1 * ${BLEED})`,
    ball: 50,
    bx: `calc(100% - ${BLEED} - 16px)`,
    mr: `calc(${BLEED} + 16px)`,
    light: 'spots',
    label: 12,
    pad: `0 0 0 ${BLEED}`,
    justify: 'center',
  },
};

/** Everything the room draws at progress q. */
export function roomFrame(q, g) {
  const iris = outExpo(seg(q, 0.03, 0.18)) * (1 - outExpo(seg(q, 0.9, 1)));
  const ballP = seg(q, 0.12, 0.32);
  const env = seg(q, 0.14, 0.32) * (1 - seg(q, 0.8, 0.94));
  const chars = Math.round(seg(q, 0.4, 0.62) * LABEL.length);
  const fringe = 1 - seg(q, 0.56, 0.72);

  const frame = {
    clip: `inset(${((1 - iris) * 50).toFixed(2)}% 0 ${((1 - iris) * 50).toFixed(2)}% 0)`,
    ballOpacity: ballP,
    ballScale: 0.68 + 0.32 * outBack(ballP),
    label: LABEL.slice(0, chars) || ' ',
    fringe,
    subOpacity: seg(q, 0.62, 0.74),
    beams: [],
    spots: [],
  };

  if (g.light === 'beams') {
    const len = (g.len0 ?? 44) + (g.len1 ?? 330) * outCubic(seg(q, 0.14, 0.58));
    frame.beams = TINTS.map((color, i) => ({ color, width: len, rotate: q * 220 + i * 90, opacity: env }));
  } else {
    const p = seg(q, 0.12, 0.92);
    frame.spots = SPOTS.map((s, i) => {
      const d = (s.o + s.v * p * 1.7) % 1.15;
      return { ...s, color: TINTS[i % 4], d, opacity: env * seg(d, 0, 0.06) };
    });
  }
  return frame;
}

/** The full-screen room scaled to this viewport: the ball fills most of the
 * short side (capped), the beams reach past the far corner, the type grows on
 * wide screens. */
export function fullscreenGeometry(w, h) {
  const g = GEOMETRY.fullscreen;
  const short = Math.min(w, h);
  return {
    ...g,
    ball: Math.round(Math.min(short * 0.62, 420)),
    len1: Math.round(Math.hypot(w, h)),
    bh: short < 600 ? 22 : 28,
    label: w < 640 ? 20 : 32,
    sub: w < 640 ? 12 : 14,
    pad: w < 640 ? g.pad : '0 0 calc(68px + env(safe-area-inset-bottom)) 56px',
  };
}
